import type { ApolloQueryResult } from 'apollo-client';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import { TemplateMediaSessionDownloadQuery } from './TemplateMediaSessionDownloadQuery.graphql';
import type {
	TemplateMediaSessionDownloadQuery as TemplateMediaSessionDownloadQueryType,
	TemplateMediaSessionDownloadQueryVariables,
} from './__types__/TemplateMediaSessionDownloadQuery';
import { TemplateMediaSessionUploadQuery } from './TemplateMediaSessionUploadQuery.graphql';
import type {
	TemplateMediaSessionUploadQuery as TemplateMediaSessionUploadQueryType,
	TemplateMediaSessionUploadQueryVariables,
} from './__types__/TemplateMediaSessionUploadQuery';

type TokenQuery = {
	spaceKey?: string | null;
	templateIds: string[];
	collectionId?: string | null;
};

/**
 * spacekey - global vs space level
 * templateIds - string[] - for previews of multiple templates
 * want to count UUID as normal template
 * templateIds - string - to differntiate between preview of single template and copy pasting
 * want to count UUID as new template in the case of uploads
 * collectionId - passed since templates that don't have static identifiers until they are saved with an image
 **/
export const getTemplateDownloadToken = async ({
	spaceKey,
	templateIds,
	collectionId = null,
}: TokenQuery): Promise<ApolloQueryResult<TemplateMediaSessionDownloadQueryType> | undefined> => {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return getApolloClient()
		.query<TemplateMediaSessionDownloadQueryType, TemplateMediaSessionDownloadQueryVariables>({
			query: TemplateMediaSessionDownloadQuery,
			variables: {
				spaceKey: spaceKey || null,
				templateIds,
				collectionId,
			},
			fetchPolicy: 'network-only',
		})
		.catch((error: Error) => {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PAGE_MODES,
			});
			markErrorAsHandled(error);
			return undefined;
		});
};

export const getTemplateUploadToken = async ({
	spaceKey,
	templateIds,
	collectionId = null,
}: TokenQuery): Promise<ApolloQueryResult<TemplateMediaSessionUploadQueryType> | undefined> => {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	return getApolloClient()
		.query<TemplateMediaSessionUploadQueryType, TemplateMediaSessionUploadQueryVariables>({
			query: TemplateMediaSessionUploadQuery,
			variables: {
				spaceKey: spaceKey || null,
				templateIds,
				collectionId,
			},
			fetchPolicy: 'no-cache',
		})
		.catch((error: Error) => {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PAGE_MODES,
			});
			markErrorAsHandled(error);
			return undefined;
		});
};
